import {
  updateProgramMemberships,
  MembershipApiError,
  CONFIRM_SCOPED_ACCESS_REMOVAL
} from "../Memberships/membershipAPI";
import { ProgramRole } from "../../API";

// SE-1723: execute a Sys-Admin bulk-end over the selection tray. Each target is
// ONE user's memberships-to-end; they go through the existing per-user
// update-program-memberships endpoint (which enforces the SE-1724 involvement
// floor + Sys-Admin gate server-side). Floor rejections (400 + earliestEndDate)
// are reported separately so the UI can tell the admin which users couldn't be
// ended at the chosen date.

export type BulkEndMembership = {
  id: string;
  programId: string;
  programRole: ProgramRole;
  startDate?: string | null;
  programName?: string | null;
};

export type BulkEndTarget = {
  userId: string;
  userLabel: string;
  memberships: BulkEndMembership[];
};

export type BulkEndOutcome = {
  succeeded: { userId: string; userLabel: string; count: number }[];
  floorRejected: {
    userId: string;
    userLabel: string;
    earliestEndDate?: string;
  }[];
  // SE-3027 × SE-1723: ending a Coach / Rotation Director also removes their
  // directorships / mentees, which the backend refuses (409 +
  // CONFIRM_SCOPED_ACCESS_REMOVAL) unless the caller confirms — a per-user
  // confirmation the bulk flow can't show. These are reported separately so the
  // admin ends them individually in Edit User, NOT lumped into "failed".
  scopedRefused: { userId: string; userLabel: string }[];
  failed: { userId: string; userLabel: string; message: string }[];
};

// Run at most CONCURRENCY per-user calls at a time. Each request is one user's
// memberships (small body), so the WAF size limit isn't a concern here — the cap
// just avoids firing hundreds of requests at once.
const CONCURRENCY = 25;

export async function applyBulkEnd(
  targets: BulkEndTarget[],
  endDateIso: string,
  api: typeof updateProgramMemberships = updateProgramMemberships
): Promise<BulkEndOutcome> {
  const outcome: BulkEndOutcome = {
    succeeded: [],
    floorRejected: [],
    scopedRefused: [],
    failed: []
  };
  for (let i = 0; i < targets.length; i += CONCURRENCY) {
    const slice = targets.slice(i, i + CONCURRENCY);
    await Promise.all(
      slice.map(async (t) => {
        try {
          await api({
            userId: t.userId,
            programMemberships: t.memberships.map((m) => ({
              id: m.id,
              programId: m.programId,
              programRole: m.programRole,
              startDate: m.startDate ?? undefined,
              endDate: endDateIso
            }))
          });
          outcome.succeeded.push({
            userId: t.userId,
            userLabel: t.userLabel,
            count: t.memberships.length
          });
        } catch (err) {
          if (
            err instanceof MembershipApiError &&
            err.status === 400 &&
            err.earliestEndDate
          ) {
            outcome.floorRejected.push({
              userId: t.userId,
              userLabel: t.userLabel,
              earliestEndDate: err.earliestEndDate
            });
          } else if (
            err instanceof MembershipApiError &&
            err.code === CONFIRM_SCOPED_ACCESS_REMOVAL
          ) {
            // A scoped-role (Coach / Rotation Director) end needs the per-user
            // "remove assignments?" confirmation the bulk flow can't show. Keep
            // it OUT of "failed" so the admin knows to end it in Edit User.
            outcome.scopedRefused.push({
              userId: t.userId,
              userLabel: t.userLabel
            });
          } else {
            outcome.failed.push({
              userId: t.userId,
              userLabel: t.userLabel,
              message: err instanceof Error ? err.message : String(err)
            });
          }
        }
      })
    );
  }
  return outcome;
}

// SE-1724 convention: ending "on" a calendar day = noon UTC, so it renders on
// that day in US timezones instead of the day before.
export function toNoonUtcIso(d: Date): string {
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)
  ).toISOString();
}

// The end date to SEND for a chosen calendar day.
//
// An "end as of TODAY" must carry the ACTUAL current timestamp — NOT noon UTC.
// The backend's back-date authz gate compares the end date against Date.now()
// (with a 5-min skew); a noon-UTC "today" reads as hours in the past once it's
// past ~12:05 UTC (~7am ET) and gets rejected as a non-SysAdmin back-date —
// which broke the PA end-membership flow every afternoon. Back-dated picks (a
// PAST calendar day) still use noon UTC so they render on the intended day.
export function toEndDateIso(picked: Date): string {
  const now = new Date();
  const isToday =
    picked.getFullYear() === now.getFullYear() &&
    picked.getMonth() === now.getMonth() &&
    picked.getDate() === now.getDate();
  return isToday ? now.toISOString() : toNoonUtcIso(picked);
}
